import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import useBrand, { getAnonymousSessionIdFromLocalStorage, removeAnonymousSessionIdFromLocalStorage } from './useBrand'
import useMaestroChat from './useMaestroChat'
import Axios from 'helpers/Interceptor'

// Define workspace types as an enum for type safety
export enum WorkspaceType {
    MARKETING_PLAN = 'marketingPlan',
    WEBSITE = 'website',
    TEMPLATE_SELECTION = 'templateSelection',
    CAMPAIGN_APPROVAL = 'campaignPreview',
    FACEBOOK_CONNECT = 'facebookConnect',
    INSTAGRAM_POST_SELECTION = 'instagramPostSelection',
    CONTENT_ANALYSIS = 'contentAnalysis',
    TASK_LIMIT = 'taskLimit',
    FEATURE_GATE = 'featureGate'
}

// Predefined configs for each workspace type
export const WORKSPACE_SIGNUP_CONFIGS: Record<WorkspaceType, SignupFlowConfig> = {
    [WorkspaceType.MARKETING_PLAN]: {
        title: 'Unlock Your Complete Marketing Plan',
        subtitle: 'Sign up to access your full strategic roadmap with detailed actions and actionable steps',
        ctaText: 'Sign Up to Unlock',
        icon: 'wand',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Complete marketing strategy breakdown',
            'Detailed action plans with timelines',
            'Symphony tool integrations',
            'Budget recommendations'
        ]
    },
    [WorkspaceType.CAMPAIGN_APPROVAL]: {
        title: 'Launch Your Campaign',
        subtitle: 'Sign up to launch your campaign and start reaching your audience',
        ctaText: 'Sign Up to Launch',
        icon: 'marketing',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Launch approved campaigns',
            'Real-time performance tracking',
            'Automated optimization',
            'Detailed analytics'
        ]
    },
    [WorkspaceType.CONTENT_ANALYSIS]: {
        title: 'Unlock Full Content Analysis',
        subtitle: 'Sign up to see complete insights and recommendations for your content',
        ctaText: 'Sign Up for Analysis',
        icon: 'analytics',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Complete content breakdown',
            'Performance recommendations',
            'Audience insights',
            'Optimization suggestions'
        ]
    },
    [WorkspaceType.TASK_LIMIT]: {
        title: 'Unlock More Tasks',
        subtitle: 'Sign up to get your first task for free and access Maestro\'s full capabilities',
        ctaText: 'Sign Up for More Tasks',
        icon: 'user',
        storageKeyPrefix: 'maestro-upgrade',
        features: [
            'Unlimited task generation',
            'Priority support',
            'Advanced AI features',
            'Custom workflows'
        ]
    },
    [WorkspaceType.WEBSITE]: {
        title: 'Save Your Website',
        subtitle: 'Sign up to save your website and publish it to the world',
        ctaText: 'Sign Up to Save',
        icon: 'website',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Custom domain publishing',
            'Website analytics',
            'SEO optimization',
            'Mobile responsiveness'
        ]
    },
    [WorkspaceType.TEMPLATE_SELECTION]: {
        title: 'Access All Templates',
        subtitle: 'Sign up to unlock premium templates and customization options',
        ctaText: 'Sign Up for Templates',
        icon: 'file',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Premium template library',
            'Advanced customization',
            'Brand integration',
            'Export capabilities'
        ]
    },
    [WorkspaceType.FACEBOOK_CONNECT]: {
        title: 'Connect Your Account',
        subtitle: 'Sign up to connect your Facebook account and access advertising tools',
        ctaText: 'Sign Up to Connect',
        icon: 'facebookFlat',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Facebook ad management',
            'Audience targeting',
            'Campaign optimization',
            'Performance tracking'
        ]
    },
    [WorkspaceType.INSTAGRAM_POST_SELECTION]: {
        title: 'Select Your Content',
        subtitle: 'Sign up to select and use your Instagram content for campaigns',
        ctaText: 'Sign Up to Select',
        icon: 'instagram',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Instagram integration',
            'Content selection',
            'Automated posting',
            'Engagement tracking'
        ]
    },
    [WorkspaceType.FEATURE_GATE]: {
        title: 'Unlock Premium Features',
        subtitle: 'Sign up to access advanced features and capabilities',
        ctaText: 'Sign Up to Unlock',
        icon: 'crown',
        storageKeyPrefix: 'pre-auth',
        features: [
            'Advanced AI tools',
            'Priority processing',
            'Custom integrations',
            'Extended limits'
        ]
    }
}

export interface SignupFlowConfig {
    title: string
    subtitle: string
    ctaText: string
    icon: string
    storageKeyPrefix: string
    features: string[]
}

interface SignupFlowState {
    // Current state
    isSignupModalOpen: boolean
    currentWorkspaceType: WorkspaceType | null
    anonymousBrandEmail: string
    isLoading: boolean
    error: string | null
    
    // Additional context data
    additionalStorageData: Record<string, string>
    chatId: string | null
    temporarySessionId: string | null
    
    // Actions
    openSignupModal: (workspaceType: WorkspaceType, context?: {
        additionalStorageData?: Record<string, string>
        chatId?: string
        temporarySessionId?: string
        loginFunction?: () => Promise<void> // Optional login function for direct auth
    }) => Promise<void>
    closeSignupModal: () => void
    setAnonymousBrandEmail: (email: string) => void
    setError: (error: string | null) => void
    setLoading: (loading: boolean) => void
    
    // Post-authentication flow management
    handlePostAuthFlow: (currentUser: any, context?: {
        history?: any
    }) => Promise<boolean>
    
    // Getters
    getCurrentConfig: () => SignupFlowConfig | null
    getStorageKey: (key: string) => string
}
const useSignupFlow = create<SignupFlowState>()(
    immer((set, get) => ({
        // Initial state
        isSignupModalOpen: false,
        currentWorkspaceType: null,
        anonymousBrandEmail: '',
        isLoading: false,
        error: null,
        additionalStorageData: {},
        chatId: null,
        temporarySessionId: null,

        // Actions
        openSignupModal: async (workspaceType, context = {}) => {
            const { 
                additionalStorageData = {}, 
                chatId = null, 
                temporarySessionId = null,
                loginFunction = null
            } = context

            // Set the workspace type and context
            set(state => {
                state.currentWorkspaceType = workspaceType
                state.additionalStorageData = additionalStorageData
                state.chatId = chatId
                state.temporarySessionId = temporarySessionId
                state.error = null
            })

            // If skipModal is true or loginFunction is provided, go straight to auth
            if (loginFunction) {
                try {
                    set(state => {
                        state.isLoading = true
                    })

                    // Store session data for post-auth restoration
                    const config = WORKSPACE_SIGNUP_CONFIGS[workspaceType]
                    if (!config) throw new Error('No config found for workspace type')

                    // Store chat ID if provided
                    if (chatId) {
                        localStorage.setItem('pre-auth-chat-id', chatId)
                    }

                    // Store workspace-specific data
                    Object.entries(additionalStorageData).forEach(([key, value]) => {
                        const storageKey = `${config.storageKeyPrefix}-${key}`
                        localStorage.setItem(storageKey, value)
                    })

                    // Store workspace type for post-auth restoration
                    localStorage.setItem('pre-auth-workspace-type', workspaceType)

                    // Execute the login function if provided
                    if (loginFunction) {
                        await loginFunction()
                    } else {
                        throw new Error('No login function provided for direct login')
                    }

                } catch (error) {
                    console.error('Direct login error:', error)
                    set(state => {
                        state.error = error instanceof Error ? error.message : 'Login failed'
                    })
                    throw error
                } finally {
                    set(state => {
                        state.isLoading = false
                    })
                }
            } else {
                // Show the modal for regular signup flow
                set(state => {
                    state.isSignupModalOpen = true
                })
            }
        },

        closeSignupModal: () => {
            set(state => {
                state.isSignupModalOpen = false
                state.currentWorkspaceType = null
                state.error = null
                state.additionalStorageData = {}
                state.chatId = null
                state.temporarySessionId = null
            })
        },

        setAnonymousBrandEmail: (email) => {
            set(state => {
                state.anonymousBrandEmail = email
            })
        },

        setError: (error) => {
            set(state => {
                state.error = error
            })
        },

        setLoading: (loading) => {
            set(state => {
                state.isLoading = loading
            })
        },

        // Centralized post-authentication flow orchestrator
        handlePostAuthFlow: async (currentUser, context = {}) => {
            try {
                console.log('🚀 Starting post-auth flow')
                const { history } = context
                
                // Check for anonymous session ID
                const anonymousSessionId = getAnonymousSessionIdFromLocalStorage()
                if (!anonymousSessionId || !currentUser?.id) {
                    console.log('🔍 No anonymous brand to link - sessionId:', !!anonymousSessionId, 'userId:', !!currentUser?.id)
                    return false
                }

                // Link anonymous brand to user
                console.log('🔗 Linking anonymous brand to authenticated user:', currentUser.id)
                
                try {
                    const response = await Axios.post('/temporary-brand/link-to-user', {
                        sessionId: anonymousSessionId,
                        userId: currentUser.id
                    })

                    if (response.data?.brand) {
                        console.log('✅ Successfully linked anonymous brand:', response.data.brand)
                        
                        // Set as first session since user came from anonymous flow
                        const { setIsFirstSession } = useBrand.getState()
                        setIsFirstSession(true)
                        console.log('🎯 Set isFirstSession=true for user who came from anonymous flow')
                        
                        // Handle chat and workspace restoration
                        if (history) {
                            // Get maestro chat functions
                            const { resumeChat, setActiveWorkspace } = useMaestroChat.getState()
                            
                            // Check for stored chat and workspace IDs
                            const preAuthChatId = localStorage.getItem('pre-auth-chat-id')
                            const preAuthCampaignId = localStorage.getItem('pre-auth-campaign-id')
                            const preAuthContentAnalysisId = localStorage.getItem('pre-auth-content-analysis-id')
                            const preAuthMarketingPlanId = localStorage.getItem('pre-auth-marketing-plan-id')
                            
                            if (preAuthChatId) {
                                console.log('🔄 Restoring chat session:', preAuthChatId)
                                
                                // Resume the chat conversation
                                await resumeChat(preAuthChatId)
                                
                                // Wait a bit for chat to fully load
                                await new Promise(resolve => setTimeout(resolve, 500))
                                
                                // Restore workspace based on what was stored
                                if (preAuthCampaignId) {
                                    console.log('🔄 Restoring campaign workspace:', preAuthCampaignId)
                                    setActiveWorkspace({
                                        type: 'campaignPreview',
                                        data: {
                                            campaignId: preAuthCampaignId
                                        }
                                    })
                                } else if (preAuthContentAnalysisId) {
                                    console.log('🔄 Restoring content analysis workspace:', preAuthContentAnalysisId)
                                    setActiveWorkspace({
                                        type: 'contentAnalysis',
                                        data: {
                                            analysisId: preAuthContentAnalysisId
                                        }
                                    })
                                } else if (preAuthMarketingPlanId) {
                                    console.log('🔄 Restoring marketing plan workspace:', preAuthMarketingPlanId)
                                    setActiveWorkspace({
                                        type: 'marketingPlan',
                                        data: {
                                            planId: preAuthMarketingPlanId
                                        }
                                    })
                                }
                                
                                // Navigate to the chat
                                const urlParams = new URLSearchParams()
                                // Only add autoOpenSubscription for campaign approval
                                if (preAuthCampaignId) {
                                    urlParams.set('autoOpenSubscription', 'true')
                                }
                                
                                const chatUrl = `/chats/${preAuthChatId}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`
                                history.push(chatUrl)
                                
                                // Clean up stored IDs
                                localStorage.removeItem('pre-auth-chat-id')
                                localStorage.removeItem('pre-auth-campaign-id')
                                localStorage.removeItem('pre-auth-content-analysis-id')
                                localStorage.removeItem('pre-auth-marketing-plan-id')
                                
                                console.log('✅ Chat and workspace restoration completed')
                            }
                        }
                        
                        // Clean up anonymous session ID
                        removeAnonymousSessionIdFromLocalStorage()
                        return true
                    }
                } catch (error) {
                    console.error('Failed to link anonymous brand to user:', error)
                    // Don't throw - we want initialization to continue even if linking fails
                }
                
                console.log('ℹ️ No post-auth processing needed')
                return false
            } catch (error) {
                console.error('❌ Post-auth flow error:', error)
                return false
            }
        },


        // Getters
        getCurrentConfig: () => {
            const { currentWorkspaceType } = get()
            return currentWorkspaceType ? WORKSPACE_SIGNUP_CONFIGS[currentWorkspaceType] : null
        },

        getStorageKey: (key: string) => {
            const config = get().getCurrentConfig()
            return config ? `${config.storageKeyPrefix}-${key}` : key
        }
    }))
)

export default useSignupFlow